// OMS (warehouse / fulfillment) shipment status vocabulary. Shared so the API
// (client, enricher, problem detection) and the dashboard classify the same
// values instead of duplicating string literals.
//
// The lifecycle, in order: PENDING → READY_FOR_PICKING → PICKING_STARTED →
// PICKING_FINISHED → PACKED (handed to the carrier) → DELIVERED (terminal).
// SUSPENDED / PENDING_FOR_AS / CANCELED / REPLACED are off-lifecycle holds and
// terminals. (The gymbeam-fe enum omitted DELIVERED — confirmed present in the
// live API.)

export const OMS_STATUSES = [
  "PENDING",
  "READY_FOR_PICKING",
  "PICKING_STARTED",
  "PICKING_FINISHED",
  "PACKED",
  "DELIVERED",
  "SUSPENDED",
  "CANCELED",
  "REPLACED",
  "PENDING_FOR_AS",
] as const

export type OmsStatus = (typeof OMS_STATUSES)[number]

/** Synthetic states Nova writes to `oms_state` that aren't OMS-native. */
export const OMS_SYNTHETIC_STATES = ["not_found", "no_shipments"] as const
export type OmsSyntheticState = (typeof OMS_SYNTHETIC_STATES)[number]

/** Everything `oms_state` can hold. */
export type OmsState = OmsStatus | OmsSyntheticState

/** OMS statuses that won't advance — re-fetching an order in one of these is
 *  wasted. PACKED is intentionally NOT here: it advances to DELIVERED (the
 *  carrier leg is tracked via boxpi/tracking, but `oms_state` does move on). */
export const OMS_TERMINAL_STATUSES = ["DELIVERED", "CANCELED", "REPLACED"] as const

export function isOmsTerminal(state: string | null | undefined): boolean {
  return state != null && (OMS_TERMINAL_STATUSES as readonly string[]).includes(state.toUpperCase())
}

/** Semantic tone for an OMS state, for UI colouring and any
 *  category-based logic. Maps every native + synthetic value. */
export type OmsStateTone = "success" | "progress" | "attention" | "failure"

export function omsStateTone(state: string | null | undefined): OmsStateTone | null {
  if (!state) {
    return null
  }
  switch (state.toUpperCase()) {
    case "DELIVERED":
      return "success"
    case "CANCELED":
    case "CANCELLED":
    case "REPLACED":
      return "failure"
    case "SUSPENDED":
    case "PENDING_FOR_AS":
    case "NOT_FOUND":
    case "NO_SHIPMENTS":
      return "attention"
    // PENDING / READY_FOR_PICKING / PICKING_* / PACKED → in progress.
    default:
      return "progress"
  }
}
